/**
 * First-challenge onboarding tour — step order, per-step config, target keys, seen flag.
 * The tour is a coach-mark guide over the real rival-first create flow. The current step is DERIVED
 * from app state (route + create step + pick count + sent + beat), see firstChallengeTourStep.helper.
 * See specs/first-challenge-onboarding/spec.md.
 */

/** Ordered tour steps. `celebrate` is an auto beat (real SuccessModal) with no coach card. */
export const FC_TOUR_STEPS = [
  'create', 'rival', 'odds', 'review', 'slate', 'coins', 'send', 'celebrate', 'live', 'nav', 'wrap',
] as const;
export type FcTourStep = (typeof FC_TOUR_STEPS)[number];

/** How a step advances / what the coach card shows. */
export type FcBeatType = 'action' | 'coach' | 'auto' | 'end';

/** Which app "screen" a step belongs to (used to reset the beat index on screen change). */
export type FcScreen = 'arena' | 'create' | 'success' | 'wrap';

export interface FcStepConfig {
  step: FcTourStep;
  screen: FcScreen;
  /** Spotlight target key (registered via useWalkthroughTarget); null = full-screen / no ring. */
  targetKey: string | null;
  title: string;
  text: string;
  beatType: FcBeatType;
}

/** Spotlight target keys — one per highlightable real element. */
export const FC_TARGET_KEYS = {
  create: 'fc_create',
  rival: 'fc_rival',
  odds: 'fc_odds',
  review: 'fc_review',
  slate: 'fc_slate',
  coins: 'fc_coins',
  send: 'fc_send',
  live: 'fc_livecard',
  nav: 'fc_nav',
} as const;

// Copy: mate-at-the-pub voice. No long dashes (house style) — commas/periods only.
export const FC_STEP_CONFIG: Record<FcTourStep, FcStepConfig> = {
  create: {
    step: 'create', screen: 'arena', targetKey: FC_TARGET_KEYS.create, beatType: 'action',
    title: 'Welcome to the Arena!', text: 'Time to make your first call. Tap + to get started.',
  },
  rival: {
    step: 'rival', screen: 'create', targetKey: FC_TARGET_KEYS.rival, beatType: 'action',
    title: 'Pick your rival', text: "Monkey's up first. Tap the card to call them out.",
  },
  odds: {
    step: 'odds', screen: 'create', targetKey: FC_TARGET_KEYS.odds, beatType: 'action',
    title: 'Call the result', text: 'Home, Draw or Away? Tap your pick.',
  },
  review: {
    step: 'review', screen: 'create', targetKey: FC_TARGET_KEYS.review, beatType: 'action',
    title: 'Locked in', text: "That's your first call. Tap Review to see your slate.",
  },
  slate: {
    step: 'slate', screen: 'create', targetKey: FC_TARGET_KEYS.slate, beatType: 'coach',
    title: 'Your slate', text: "Here's what you've called. Remove any pick with the ×.",
  },
  coins: {
    step: 'coins', screen: 'create', targetKey: FC_TARGET_KEYS.coins, beatType: 'coach',
    title: 'Your stake', text: "Monkey plays for pride, so this one's 0 coins.",
  },
  send: {
    step: 'send', screen: 'create', targetKey: FC_TARGET_KEYS.send, beatType: 'action',
    title: 'Send it', text: 'Happy with your slate? Fire the challenge over to Monkey.',
  },
  celebrate: {
    step: 'celebrate', screen: 'success', targetKey: null, beatType: 'auto',
    title: '', text: '',
  },
  live: {
    step: 'live', screen: 'arena', targetKey: FC_TARGET_KEYS.live, beatType: 'coach',
    title: "You're live!", text: 'Your challenge is on. It settles the moment the match ends.',
  },
  nav: {
    step: 'nav', screen: 'arena', targetKey: FC_TARGET_KEYS.nav, beatType: 'coach',
    title: 'Your home base', text: 'Arena for duels, Fixtures for games, Dashboard for stats, WC 2026 soon.',
  },
  wrap: {
    // screen is 'arena' (not 'wrap') on purpose: wrap is the final beat of the arena-live sequence
    // (live → nav → wrap), shown while still on the Arena route. If it declared a distinct screen the
    // overlay's screen-change beat reset would fire on nav → wrap, snapping the beat back to 0 (live)
    // and making wrap unreachable. The wrap card renders off `step === 'wrap'`, not the screen.
    step: 'wrap', screen: 'arena', targetKey: null, beatType: 'end',
    title: "That's a wrap!", text: "You've made your first call. Now go climb that leaderboard.",
  },
};

/**
 * Guest-only sign-up CTA on the closing wrap card — the true end of onboarding, and where we
 * ask a guest to upgrade. Honest carrot only: conversion floors coins to 1000
 * (specs/authentication), so we promise coins + friends + full experience. We never say "keep
 * the coins you won" (there is no pending-coins infra; mini-game coins are computed, not banked).
 */
export const FC_WRAP_GUEST_CTA = {
  /** Benefit sub-line shown to guests on the wrap card. */
  benefits: 'Sign up to grab 1000 coins, add friends, and unlock the full experience.',
  /** Primary button label for guests. */
  signUp: 'Save my progress',
  /** Analytics `action` label for the wrap-card sign-up prompt (shown + accepted). */
  action: 'onboarding_wrap',
} as const;

/** Steps that count toward the "n / N" progress (everything the user actually sees a coach card for). */
export const FC_SHOWN_STEPS: FcTourStep[] = FC_TOUR_STEPS.filter((s) => s !== 'celebrate');

/** Beat sequences for the two screens that hold more than one coach beat. */
export const FC_REVIEW_BEATS: FcTourStep[] = ['slate', 'coins', 'send'];
export const FC_ARENA_LIVE_BEATS: FcTourStep[] = ['live', 'nav', 'wrap'];

/** AsyncStorage/localStorage key — DISTINCT from questionnaire-seen so a returning guest skips the tour. */
export const FIRST_CHALLENGE_TOUR_SEEN_KEY = 'has_seen_first_challenge_tour';
